import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { REDUCED_MOTION_DIALOG_CLASS } from '../components/ui/modalScrim';
import { ShopifyPermissionsList } from './ShopifyPermissionsList';
import { SHOPIFY_PERMISSIONS } from './shopifyScopes';
import type { RefObject } from 'react';

/** The scopes and the install note, behind a button instead of inline on the
 *  integrations card — where together they left the card far taller than its
 *  siblings. Passes no `granted`: nothing is granted until a store connects. */
export function ShopifyPermissionsDialog({
  open,
  onOpenChange,
  returnFocusTo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The control that opened this. Radix's modal content preventDefaults the
   *  focus scope's own restore and focuses its internal `triggerRef`, which is
   *  null without a `DialogTrigger` — so without this, closing dropped focus
   *  to `<body>`. */
  returnFocusTo?: RefObject<HTMLElement | null>;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* max-h/overflow: six rows plus the note overflow a 400%-zoom viewport,
          and a centred fixed panel clips at both ends with no way to scroll. */}
      <DialogContent
        data-clarity-mask="True"
        className={`max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg ${REDUCED_MOTION_DIALOG_CLASS}`}
        onCloseAutoFocus={(event) => {
          if (!returnFocusTo?.current) return;
          event.preventDefault();
          returnFocusTo.current.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Permissions Discovr will ask for</DialogTitle>
          {/* Not "Discovr never asks for anything outside this list": Shopify's
              own install screen additionally shows "View staff and contributor
              data — Store owner", which it adds for every app and which
              shopify.app.toml does not request. A reviewer comparing the two
              screens would read the stronger claim as a contradiction. */}
          <DialogDescription>
            These are the permissions Discovr requests. Shopify may also show basic store-owner
            details, which every app can see.
          </DialogDescription>
        </DialogHeader>
        <ShopifyPermissionsList rows={SHOPIFY_PERMISSIONS} />
        {/* With SHOPIFY_APP_INSTALL_URL unset there is no Connect control, so
            this line is the only route to connecting (App Store req 2.1.1). */}
        <p className="text-muted-foreground text-caption-regular">
          Connect by installing the <span className="text-foreground font-medium">Discovr AI</span>{' '}
          app from the Shopify App Store. You'll be brought back here and your store connects
          automatically.
        </p>
      </DialogContent>
    </Dialog>
  );
}
